import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  Box,
  costFormater,
  FlexListItemOption,
  formatSnakeCaseToGeneral,
  Inscription,
  OptionsButton,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';
import { capitalize, toNumber } from 'lodash';
import styled from 'styled-components';

import { DEFAULT_DATE_FORMAT, EMPTY_FIELD_VALUE } from '@/common/constants';
import Permissions from '@/common/permissions';
import ROUTES from '@/common/routes';
import { useHasAccess } from '@/modules/core/hooks';
import { PAYMENT_METHOD_TITLE, PaymentMethodType } from '@/modules/employee/employee.types';
import { LockboxType } from '@/modules/employer/components/Lockbox/lockbox.types';
import { getFullAddress } from '@/utils/modifiers';

import { EllipsisText, OptionsButtonWrapper } from './Checks.styled';
import { Check, CheckStatus, checkStatusColor, CheckType } from './constants';
import ReissueCheckPopup from './ReissueCheckPopup';
import VoidCheckPopup from './VoidCheckPopup';

interface WrapperProps {
  expanded?: boolean;
}

const Wrapper = styled(Box)<WrapperProps>`
  ${({ theme, expanded }) =>
    expanded
      ? `
    border: 1px solid ${theme.colors.border1};
    outline: 1px solid ${theme.colors.border1};
  `
      : undefined};
  &:hover {
    outline: 1px solid ${({ theme }) => theme.colors.border1};
    border: 1px solid ${({ theme }) => theme.colors.border1};
  }
`;

const DEFAULT_CLAIMS_SHOWED = 3;
const SEPARATOR = '-';
const CLAIM_MASK = `9${SEPARATOR}999${SEPARATOR}999`;
const formatId = (claimId: string = '') =>
  claimId
    .split('')
    .map((symbol, index) => {
      if (index === CLAIM_MASK.indexOf(SEPARATOR)) return SEPARATOR.concat(symbol);
      if (index === CLAIM_MASK.indexOf(SEPARATOR, 2) - 1) return SEPARATOR.concat(symbol);
      return symbol;
    })
    .join('');

interface Props {
  check: Check;
  setOpenCheckId: (id: string) => void;
  openCheckId?: string;
  setSearchCheckId: React.Dispatch<React.SetStateAction<string>>;
  clearFilters: () => void;
}

const CheckRow: React.FC<Props> = ({
  check,
  setOpenCheckId,
  openCheckId,
  clearFilters,
  setSearchCheckId,
}) => {
  const [hasVoidCheckPopup, openVoidCheckPopup] = useState(false);
  const [hasReissueCheckPopup, openReissueCheckPopup] = useState(false);
  const hasAccessToVoidingReissue = useHasAccess([{ permission: Permissions.VOID_REISSUE_CHECK }]);
  const hasPermissionToOpenProfile = useHasAccess([{ permission: Permissions.VIEW_PROFILE }]);
  const hasVoidOption = useMemo(() => {
    if (
      check?.type === CheckType.LOCKBOX &&
      (check?.lockboxType === LockboxType.OUTBOUND_TOA ||
        check?.lockboxType === LockboxType.INBOUND_TOA ||
        check?.lockboxType === LockboxType.CLAIM_REPAYMENT) &&
      check.status === CheckStatus.ISSUED
    ) {
      return true;
    }
    return (
      hasAccessToVoidingReissue &&
      check?.type !== CheckType.LOCKBOX &&
      (check.status === CheckStatus.PENDING || check.status === CheckStatus.ISSUED)
    );
  }, [check, hasAccessToVoidingReissue]);
  const hasReissueOption = useMemo(
    () =>
      (check.status === CheckStatus.ISSUED || check.status === CheckStatus.REISSUE_REQUIRED) &&
      hasAccessToVoidingReissue,
    [check, hasAccessToVoidingReissue]
  );
  const isReturned = useMemo(() => check.status === CheckStatus.RETURNED, [check.status]);
  const [areAllClaimsShowed, showAllClaims] = useState(
    (check?.claimIds?.length || 0) <= DEFAULT_CLAIMS_SHOWED
  );
  const expanded = useMemo(() => check?.id === openCheckId, [check, openCheckId]);
  useEffect(() => {
    if (areAllClaimsShowed && !expanded) {
      showAllClaims(false);
    }
  }, [expanded, areAllClaimsShowed, showAllClaims]);
  const amount = useMemo(() => (check.amount ? costFormater(check.amount, true) : ''), [check.amount]);
  const issuedDate = useMemo(
    () => (check.issuedDate ? dayjs(check.issuedDate).format(DEFAULT_DATE_FORMAT) : ''),
    [check.issuedDate]
  );
  const statusDate = useMemo(
    () => (check.statusDate ? dayjs(check.statusDate).format(DEFAULT_DATE_FORMAT) : ''),
    [check.statusDate]
  );
  const status = useMemo(() => formatSnakeCaseToGeneral(check.status || ''), [check.status]);
  const statusColor = useMemo(() => (check?.status ? checkStatusColor[check.status] : ''), [check.status]);
  const address = useMemo(() => getFullAddress(check?.address), [check.address]);
  const history = useHistory();
  const expand = useCallback(
    () => setOpenCheckId(expanded ? '' : check?.id || ''),
    [expanded, check, setOpenCheckId]
  );
  const reissuedText = useMemo(() => {
    if (!check?.reissuePaymentMethodType) return '';
    const formattedPaymentMethod = PAYMENT_METHOD_TITLE[check.reissuePaymentMethodType];
    if (check?.reissueCheckId) return `${formattedPaymentMethod} #${check?.reissueCheckId}`;
    return formattedPaymentMethod;
  }, [check]);
  const createdWith = useMemo(() => formatSnakeCaseToGeneral(check?.type || ''), [check?.type]);
  const handleOpenProfile = () => {
    window.open(`${ROUTES.EMPLOYEE_VIEW(check.employeeId)}${history.location.search}`, '_blank');
  };
  return (
    <Wrapper
      data-testId="check-row"
      round="container2Round"
      background="canvas"
      border={{ side: 'all', size: '1px', color: 'border2' }}
      pad={{ horizontal: 'spacing24', vertical: '18px' }}
      margin={{ top: 'spacing8' }}
      expanded={expanded}
    >
      <VoidCheckPopup id={check.id} isVisible={hasVoidCheckPopup} close={() => openVoidCheckPopup(false)} />
      <ReissueCheckPopup
        isVisible={hasReissueCheckPopup}
        close={() => openReissueCheckPopup(false)}
        check={check}
      />
      <Box direction="row" align="center">
        <Box data-testId="check-row_expand_button" onClick={expand}>
          {expanded ? <ArrowUpIcon color="iconPrimary" /> : <ArrowDownIcon color="iconPrimary" />}
        </Box>
        <Box
          data-testId="check-row_check-id"
          margin={{ left: '25px' }}
          width="100px"
          justify="center"
          onClick={expand}
        >
          <Inscription weight="bold" lineHeight="20px">
            {check.id || EMPTY_FIELD_VALUE}
          </Inscription>
        </Box>
        <Box data-testId="check-row_amount" width="104px" justify="center" onClick={expand}>
          <Inscription lineHeight="20px">{amount || EMPTY_FIELD_VALUE}</Inscription>
        </Box>
        <Box data-testId="check-row_employee-name" width="195px" justify="center">
          <Box direction="row">
            <EllipsisText
              lineHeight="20px"
              title={check.employeeName}
              cursor="pointer"
              {...(check.employeeId && check.employeeName && hasPermissionToOpenProfile
                ? {
                    onClick: handleOpenProfile,
                    color: 'textAccent',
                  }
                : { onClick: expand })}
            >
              {check.employeeName || EMPTY_FIELD_VALUE}
            </EllipsisText>
            <Box flex onClick={expand} />
          </Box>
        </Box>
        <Box data-testId="check-row_employer-name" width="154px" justify="center" onClick={expand}>
          <EllipsisText lineHeight="20px" {...(check.employerName ? { title: check.employerName } : {})}>
            {check.employerName || EMPTY_FIELD_VALUE}
          </EllipsisText>
        </Box>
        <Box data-testId="check-row_issued-date" width="98px" justify="center" onClick={expand}>
          <EllipsisText lineHeight="20px" {...(issuedDate ? { title: issuedDate } : {})}>
            {issuedDate || EMPTY_FIELD_VALUE}
          </EllipsisText>
        </Box>
        <Box data-testId="check-row_status-date" width="130px" justify="center" onClick={expand}>
          <EllipsisText lineHeight="20px" {...(statusDate ? { title: statusDate } : {})}>
            {statusDate || EMPTY_FIELD_VALUE}
          </EllipsisText>
        </Box>
        <Box
          data-testId="check-row_status"
          width="138px"
          justify="between"
          direction="row"
          align="center"
          onClick={expand}
        >
          <EllipsisText data-testId="check-row_row_status-value" lineHeight="20px" title={status}>
            {status || EMPTY_FIELD_VALUE}
          </EllipsisText>
          {statusColor ? (
            <Box
              data-testId="check-row_row_status-circle"
              width={{ width: '10px', min: '10px' }}
              height="10px"
              margin={{ left: 'spacing8', right: 'spacing24' }}
              round
              background={statusColor}
            />
          ) : null}
        </Box>
        {!isReturned ? (
          <OptionsButtonWrapper data-testId="check-row_options">
            <OptionsButton
              disabled={!hasVoidOption && !hasReissueOption}
              {...(hasVoidCheckPopup || hasReissueCheckPopup ? { isOpen: false } : {})}
            >
              {hasVoidOption ? (
                <FlexListItemOption
                  name="Void"
                  testId="Void"
                  onClick={() => {
                    setOpenCheckId(check?.id || '');
                    openVoidCheckPopup(true);
                  }}
                  optionStyle={{ minWidth: '135.91px' }}
                />
              ) : null}
              {hasReissueOption ? (
                <FlexListItemOption
                  name="Reissue"
                  testId="Reissue"
                  onClick={() => {
                    setOpenCheckId(check?.id || '');
                    openReissueCheckPopup(true);
                  }}
                  optionStyle={{ minWidth: '135.91px' }}
                />
              ) : null}
            </OptionsButton>
          </OptionsButtonWrapper>
        ) : null}
      </Box>
      {expanded ? (
        <Box
          data-testId="check-sub-row"
          border={{ color: 'border1', side: 'top' }}
          pad={{ top: 'spacing16' }}
          margin={{ top: 'spacing16' }}
          align="baseline"
          direction="row"
        >
          <Box data-testId="check-sub-row_sent-to" width="254px" height="38px" justify="center">
            <Box width="230px">
              <EllipsisText lineHeight="18px" size="12px" weight="bold">
                Check Sent to
              </EllipsisText>
              <EllipsisText lineHeight="20px" title={address}>
                {address || EMPTY_FIELD_VALUE}
              </EllipsisText>
            </Box>
          </Box>
          <Box data-testId="check-sub-row_pay-to" width="254px" height="38px" justify="center">
            <Box width="230px">
              <EllipsisText lineHeight="18px" size="12px" weight="bold">
                Pay To
              </EllipsisText>
              <EllipsisText lineHeight="20px" title={check?.payTo}>
                {check?.payTo || EMPTY_FIELD_VALUE}
              </EllipsisText>
            </Box>
          </Box>
          {check?.type === CheckType.LOCKBOX ? (
            <Box data-testId="check-sub-row_related-lockbox" width="280px" justify="center">
              <Box width="256px">
                <EllipsisText lineHeight="18px" size="12px" weight="bold">
                  Related Lockbox
                </EllipsisText>
                <Box>
                  <EllipsisText
                    lineHeight="20px"
                    {...(check?.lockboxId
                      ? {
                          onClick: () => window.open(`${ROUTES.LOCKBOX_DETAILS(check?.lockboxId)}`, '_blank'),
                          cursor: 'pointer',
                          color: 'info',
                        }
                      : { color: 'textTitle' })}
                  >
                    {formatId(check?.lockboxId) || EMPTY_FIELD_VALUE}
                  </EllipsisText>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box data-testId="check-sub-row_related-claims" width="280px" justify="center">
              <Box width="256px">
                <EllipsisText lineHeight="18px" size="12px" weight="bold">
                  Related Claims
                </EllipsisText>
                <Box>
                  {check?.claimIds?.length ? (
                    check.claimIds.map((id, index) => {
                      const isShown = areAllClaimsShowed || index < DEFAULT_CLAIMS_SHOWED;
                      const isLastItem = index === toNumber(check?.claimIds?.length) - 1;
                      if (isLastItem && !isShown)
                        return (
                          <EllipsisText
                            key={id}
                            data-testId="check-sub-row_show-more-claims_button"
                            lineHeight="20px"
                            cursor="pointer"
                            color="info"
                            onClick={() => showAllClaims(true)}
                          >
                            {`Show More (${toNumber(check?.claimIds?.length) - DEFAULT_CLAIMS_SHOWED})`}
                          </EllipsisText>
                        );
                      if (!isShown) return null;
                      const formattedClaimId = formatId(id);
                      return (
                        <EllipsisText
                          key={id}
                          data-testId="check-sub-row_related-claim"
                          lineHeight="20px"
                          title={formattedClaimId}
                          {...(id
                            ? {
                                onClick: () =>
                                  window.open(
                                    `${ROUTES.CLAIM_PROCESS}/${id}${history.location.search}`,
                                    '_blank'
                                  ),
                                cursor: 'pointer',
                                color: 'info',
                              }
                            : { color: 'textTitle' })}
                        >
                          {formattedClaimId || EMPTY_FIELD_VALUE}
                        </EllipsisText>
                      );
                    })
                  ) : (
                    <EllipsisText color="textTitle" lineHeight="20px">
                      {EMPTY_FIELD_VALUE}
                    </EllipsisText>
                  )}
                </Box>
              </Box>
            </Box>
          )}
          {check?.voidReason ? (
            <Box data-testId="check-sub-row_void-reason" width="280px" justify="center">
              <Box width="256px">
                <EllipsisText lineHeight="18px" size="12px" weight="bold">
                  Void Reason
                </EllipsisText>
                <EllipsisText color="textTitle" lineHeight="20px">
                  {capitalize(check.voidReason.replace(/_/g, ' '))}
                </EllipsisText>
              </Box>
            </Box>
          ) : null}
          {check?.reissuePaymentMethodType ? (
            <Box data-testId="check-sub-row_reissued-with" width="280px" justify="center">
              <Box width="256px">
                <EllipsisText lineHeight="18px" size="12px" weight="bold">
                  Reissued with
                </EllipsisText>
                <Box>
                  <EllipsisText
                    key={reissuedText}
                    lineHeight="20px"
                    title={reissuedText}
                    {...(check?.reissueCheckId && check.reissuePaymentMethodType === PaymentMethodType.CHECK
                      ? {
                          onClick: () => {
                            clearFilters();
                            setSearchCheckId(check.reissueCheckId as React.SetStateAction<string>);
                          },
                          cursor: 'pointer',
                          color: 'info',
                        }
                      : { color: 'textTitle' })}
                  >
                    {reissuedText}
                  </EllipsisText>
                </Box>
              </Box>
            </Box>
          ) : null}
          <Box data-testId="check-sub-row_created-with" width="180px" justify="center">
            <Box width="180px">
              <EllipsisText lineHeight="18px" size="12px" weight="bold">
                Created with
              </EllipsisText>
              <Box>
                <EllipsisText key={createdWith} lineHeight="20px" title={createdWith}>
                  {createdWith || EMPTY_FIELD_VALUE}
                </EllipsisText>
              </Box>
            </Box>
          </Box>
        </Box>
      ) : null}
    </Wrapper>
  );
};

export default CheckRow;
